import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DatePickerCustom from '@/components/custom-date-picker';
import { useServiceOrders } from '@/hooks/api-hooks';
import { setWebTitle, todayStart, getUrlParam, updateUrlParams } from '@/lib/format-utils';
import { SkeletonList } from '@/components/snippets';
import SearchInput from '@/components/search-input';
import { ServiceOrder } from '@turizi/shared';
import { ServiceOrderToString } from '@/lib/models-utils';
import ManageServiceOrderCard from '@/components/service-orders/manage-service-order-card';
import { Button } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';


export default function ServiceOrdersPage(): JSX.Element {
    const { t } = useTranslation();

    const [dateRange, setDateRange] = useState<[string | null, string | null]>([
        getUrlParam('start_date', todayStart()),
        getUrlParam('end_date', todayStart())
    ]);
    const [searchText, setSearchText] = useState<string>('');

    setWebTitle(t('service_orders'));

    const {
        data: ordersData,
        isLoading: loadingOrders,
        refetch: refetchOrders
    } = useServiceOrders({
        start_date: dateRange[0],
        end_date: dateRange[1],
    }, {
        enabled: !!dateRange[0] && !!dateRange[1]
    });
    const serviceOrders = ordersData?.data || [];

    useEffect(() => {
        updateUrlParams({
            start_date: dateRange[0],
            end_date: dateRange[1],
        });
    }, [dateRange]);

    const [expandedOrders, setExpandedOrders] = useState<Record<number, boolean>>({});

    const filteredServiceOrders = useMemo(() => {
        let filtered = (serviceOrders || []);
        if (searchText) {
            filtered = filtered.filter((so: ServiceOrder) => ServiceOrderToString(so).includes(searchText.toLowerCase()));
        }
        return filtered;
    }, [serviceOrders, searchText]);

    const anyExpanded = useMemo(() => {
        return filteredServiceOrders.some(so => expandedOrders[so.id] !== false);
    }, [filteredServiceOrders, expandedOrders]);

    const handleToggleOrderBookings = (id: number) => {
        setExpandedOrders(prev => {
            const current = prev[id] !== false;
            return {
                ...prev,
                [id]: !current
            };
        });
    };

    const handleToggleAll = () => {
        const nextState = !anyExpanded;
        const newExpanded: Record<number, boolean> = {};
        filteredServiceOrders.forEach(so => {
            newExpanded[so.id] = nextState;
        });
        setExpandedOrders(newExpanded);
    };

    const handleRefresh = () => {
        refetchOrders();
    };

    return (
        <>
            <div className="panel-content space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <DatePickerCustom
                            hideLabel
                            className='font-bold'
                            clearable={false}
                            value={dateRange}
                            onChange={(val) => setDateRange(val)}
                            loading={loadingOrders}
                        />
                        <SearchInput
                            className='flex-1'
                            onSearch={(text) => setSearchText(text)}
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            variant="subtle"
                            size="xs"
                            color="gray"
                            leftSection={anyExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                            onClick={handleToggleAll}
                            className="font-semibold"
                        >
                            {anyExpanded ? t('collapse_all') : t('expand_all')}
                        </Button>
                        <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                            {serviceOrders && serviceOrders.length}
                        </span>
                    </div>
                </div>
                {filteredServiceOrders.map((serviceOrder) => (
                    <ManageServiceOrderCard
                        key={serviceOrder.id}
                        serviceOrder={serviceOrder}
                        showBookings={expandedOrders[serviceOrder.id] !== false}
                        onToggleShowBookings={() => handleToggleOrderBookings(serviceOrder.id)}
                    />
                ))}
            </div>
        </>
    );
}
